import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { toast } from "sonner";
import {
  FileText, Clock, CalendarDays, Truck, Bell, Users, Package, Globe,
  FileCheck, CreditCard, FilePlus, UserPlus, AlertTriangle, LayoutDashboard,
  Wallet, Container, Plane, Warehouse, ShoppingBag,
  Activity, Server, MessageSquare, MailOpen, Radar,
  RefreshCw, CheckCircle2, XCircle, TrendingUp, Star,
} from "lucide-react";
import EmailDashboardWidget from "../EmailDashboardWidget";
import ProductionConfigWidget from "../ProductionConfigWidget";
import DebiteurenDashboardCard from "./DebiteurenDashboardCard";
import StripeInsightsWidget from "../StripeInsightsWidget";
import CommunicatieInsightsWidget from "../CommunicatieInsightsWidget";
import TrackingUpdaterWidget from "./TrackingUpdaterWidget";
import { openShipment } from "../../../utils/shipmentNavigation";
import TrackingDashboardWidget from "./TrackingDashboardWidget";
import SystemHealthWidget from "./SystemHealthWidget";
import RecentDocumentsWidget from "../RecentDocumentsWidget";
import { AdminPageHeader, AdminKpiCard, AdminSectionCard, AdminSkeleton } from "../ui";
import { useUserPreferences } from "../../../context/UserPreferencesContext";
import { PlatformIdentity } from "../../platform/PlatformIdentity";

/*
 * DashboardSection — Sprint A Finalisatie (22 Feb 2026).
 *
 * Herindeling per user-spec: 6 samenhangende blokken i.p.v. één grote KPI-grid.
 *   1. Vandaag       — dagelijkse activiteit (nieuwe klanten/boekingen/etc.)
 *   2. Warehouse     — binnen/uit + pickups/deliveries + open opdrachten
 *   3. Logistiek     — containers/vluchten/ETA/ETD/tracking-updates
 *   4. Financieel    — openstaand/betaald/achterstallig/Stripe/herinneringen
 *   5. Communicatie  — email/WA/SMS/open klantvragen (via CommunicatieInsightsWidget)
 *   6. Systeemstatus — API/queue/synchronisaties/waarschuwingen + Tracking Updater
 *
 * Alle bestaande data-bronnen blijven ongewijzigd (`/admin/dashboard` endpoint).
 * De hergroepering is puur visueel — geen backend-wijziging nodig.
 */

// ─── Blok configuraties (each = list van kaarten die uit /admin/dashboard komen) ───
const BLOCKS = [
  {
    key: "vandaag",
    title: "Vandaag",
    icon: CalendarDays,
    subtitle: "Nieuwe activiteit sinds middernacht",
    tone: "brand",
    cards: [
      { key: "nieuwe_klanten_vandaag",       label: "Nieuwe klanten",         icon: UserPlus,   tone: "emerald" },
      { key: "nieuwe_bestellingen_vandaag",  label: "Nieuwe bestellingen",    icon: ShoppingBag,tone: "brand" },
      { key: "nieuwe_offertes_vandaag",      label: "Nieuwe offertes",        icon: FilePlus,   tone: "brand" },
      { key: "nieuwe_facturen_vandaag",      label: "Nieuwe facturen",        icon: FilePlus,   tone: "brand" },
      { key: "nieuwe_warehouse_vandaag",     label: "Nieuwe warehouse-ontvangsten", icon: Warehouse, tone: "slate" },
      { key: "nieuwe_communicatie_vandaag",  label: "Nieuwe communicatie",    icon: MessageSquare, tone: "info" },
    ],
  },
  {
    key: "warehouse",
    title: "Warehouse",
    icon: Warehouse,
    subtitle: "Ontvangst, verzending en pickup/delivery-planning",
    tone: "slate",
    cards: [
      { key: "warehouse_binnengekomen_vandaag", label: "Binnengekomen goederen", icon: Package,   tone: "emerald" },
      { key: "warehouse_uitgaand_vandaag",      label: "Uitgaande goederen",     icon: Truck,     tone: "brand" },
      { key: "afspraken_vandaag",               label: "Pickups vandaag",        icon: CalendarDays, tone: "info" },
      { key: "deliveries_vandaag",              label: "Deliveries vandaag",     icon: Truck,     tone: "info" },
      { key: "warehouse_actief",                label: "Open warehouse-opdrachten", icon: Warehouse, tone: "slate" },
      { key: "pickup_delivery_deze_week",       label: "Pickup & Delivery deze week", icon: Truck, tone: "brand" },
    ],
  },
  {
    key: "logistiek",
    title: "Logistiek",
    icon: Truck,
    subtitle: "Containers, vluchten, shipments en ETA/ETD",
    tone: "info",
    cards: [
      { key: "containers_onderweg",       label: "Containers onderweg",  icon: Container, tone: "info" },
      { key: "containers_sluiten_vandaag",label: "Containers sluiten vandaag", icon: Container, tone: "amber" },
      { key: "vluchten_vandaag",          label: "Vluchten vandaag",     icon: Plane,     tone: "brand" },
      { key: "shipments_actief",          label: "Shipments actief",     icon: Truck,     tone: "brand" },
      { key: "eta_deze_week",             label: "ETA deze week",        icon: TrendingUp, tone: "emerald" },
      { key: "tracking_updates_vandaag",  label: "Tracking-updates vandaag", icon: Radar, tone: "info" },
    ],
  },
  {
    key: "financieel",
    title: "Financieel",
    icon: Wallet,
    subtitle: "Facturen, betalingen, achterstallig en Stripe",
    tone: "amber",
    cards: [
      { key: "openstaande_facturen",      label: "Openstaande facturen", icon: FileText,   tone: "orange" },
      { key: "betalingen_vandaag_aantal", label: "Vandaag betaald",      icon: Wallet,     tone: "emerald", special: "amount" },
      { key: "achterstallige_facturen",   label: "Achterstallige betalingen", icon: AlertTriangle, tone: "red" },
      { key: "stripe_betalingen_vandaag", label: "Stripe-betalingen vandaag", icon: CreditCard, tone: "brand" },
      { key: "openstaande_reminders",     label: "Open herinneringen",   icon: Bell,       tone: "slate" },
      { key: "goedgekeurd_deze_maand",    label: "Goedgekeurd deze maand", icon: FileCheck, tone: "emerald" },
    ],
  },
];

// AdminKpiCard tone-safety mapping
const TONE_MAP = { info: "blue" };

// ─── FavoriteToggle — kleine star die aan/uit toggle voor dashboard-widgets ─
function FavoriteToggle({ widgetKey, testid }) {
  const { isWidgetFavorite, toggleWidgetFavorite } = useUserPreferences();
  const isFav = isWidgetFavorite(widgetKey);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        toggleWidgetFavorite(widgetKey);
      }}
      className={`p-1 rounded transition-colors ${
        isFav ? "text-amber-500 hover:text-amber-600" : "text-slate-300 hover:text-amber-400"
      }`}
      aria-label={isFav ? "Verwijder uit favorieten" : "Voeg toe aan favorieten"}
      data-testid={testid || `dash-fav-${widgetKey}`}
      title={isFav ? "Verwijder uit favorieten" : "Voeg toe aan favorieten"}
    >
      <Star className={`w-3.5 h-3.5 ${isFav ? "fill-current" : ""}`} />
    </button>
  );
}

// ─── SectionTitle met star ─
function WidgetTitle({ icon: Icon, label, widgetKey }) {
  return (
    <span className="flex items-center gap-2">
      <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </span>
      <span>{label}</span>
      <FavoriteToggle widgetKey={widgetKey} />
    </span>
  );
}

function KpiBlock({ block, cards, loading }) {
  const Icon = block.icon;
  return (
    <AdminSectionCard
      title={<WidgetTitle icon={Icon} label={block.title} widgetKey={`block-${block.key}`} />}
      description={block.subtitle}
      testid={`dash-block-${block.key}`}
    >
      {loading ? (
        <AdminSkeleton variant="kpi-grid" count={6} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {block.cards.map((c) => {
            const raw = cards[c.key];
            let value = raw ?? 0;
            if (c.special === "amount") {
              const som = cards.betalingen_vandaag_som || 0;
              value = som > 0
                ? `${raw ?? 0} · € ${Number(som).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : (raw ?? 0);
            }
            return (
              <AdminKpiCard
                key={c.key}
                label={c.label}
                value={value}
                icon={c.icon}
                tone={TONE_MAP[c.tone] || c.tone}
                testid={`dash-card-${c.key}`}
              />
            );
          })}
        </div>
      )}
    </AdminSectionCard>
  );
}

export default function DashboardSection({ setActive }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    adminApi.get("/admin/dashboard")
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const cards = data?.cards || {};
  const lastPages = data?.last_pages || [];

  return (
    <div className="space-y-6" data-testid="admin-section-dashboard">
      <PlatformIdentity context="dashboard" />
      <AdminPageHeader
        icon={LayoutDashboard}
        title="Dashboard"
        subtitle="Rustig gegroepeerd overzicht van dagelijkse operaties"
      />

      {BLOCKS.map((b) => (
        <KpiBlock key={b.key} block={b} cards={cards} loading={loading} />
      ))}

      {/* ─── Communicatie blok ─────────────────────────────────── */}
      <AdminSectionCard
        title={<WidgetTitle icon={MessageSquare} label="Communicatie" widgetKey="communicatie" />}
        description="Nieuwe e-mails, WhatsApp, SMS en open klantvragen"
        testid="dash-block-communicatie"
      >
        <CommunicatieInsightsWidget onOpenCommunicatie={() => setActive?.("communicatie")} />
      </AdminSectionCard>

      {/* ─── Tracking blok (Fase A Afronding · Feb 2026) ─────────── */}
      <AdminSectionCard
        title={<WidgetTitle icon={Radar} label="Tracking" widgetKey="tracking" />}
        description="Recente events + canonical statusverdeling laatste 7 dagen"
        testid="dash-block-tracking"
      >
        <TrackingDashboardWidget
          onOpenShipment={(ev) => {
            if (!ev?.entity_id) return;
            // P0 UX Fix (Aug 2026) — Canonical Smart Navigation
            openShipment({
              wNumber: ev?.tracking_number || ev?.w_number,
              shipmentId: ev.entity_id,
              context: ev?.context || null,
              source: "dashboard_tracking",
            });
          }}
        />
      </AdminSectionCard>

      {/* ─── Debiteuren + Stripe insight (behoud bestaande widgets) ─ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DebiteurenDashboardCard />
        <StripeInsightsWidget />
      </div>

      {/* ─── Systeemstatus blok — Fase A Afronding (Feb 2026) ────── */}
      <AdminSectionCard
        title={<WidgetTitle icon={Server} label="Systeemstatus" widgetKey="systeemstatus" />}
        description="MongoDB · Services · Resend/Stripe webhooks · Scheduler · Tracking Updater · E-mail delivery"
        testid="dash-block-systeemstatus"
      >
        <SystemHealthWidget />
        <div className="mt-4 pt-4 border-t border-slate-100">
          <TrackingUpdaterWidget />
        </div>
      </AdminSectionCard>

      {/* ─── Overige widgets (behouden — production/email/pages) ── */}
      <RecentDocumentsWidget />

      <AdminSectionCard title="Recent gewijzigde websitepagina&apos;s" testid="dash-recent-pages">
        {lastPages.length === 0 ? (
          <p className="text-sm text-ink-500">Nog geen pagina&apos;s geregistreerd. Voeg pagina&apos;s toe via Websitebeheer.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lastPages.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between text-sm">
                <span className="font-semibold text-brand-900">{p.title || p.slug}</span>
                <span className="text-xs text-ink-500 font-mono-num tabular-nums">
                  {p.updated_at?.slice(0, 16).replace("T", " ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </AdminSectionCard>

      <EmailDashboardWidget />
      <ProductionConfigWidget />
    </div>
  );
}
